if (!customElements.get('form-validator')) {
  class FormValidator extends HTMLElement {
    constructor() {
      super();
      this.form = null;
      this.fields = [];
      this.submitButton = null;
      this.invalidFields = new Set();
      this.boundOnSubmit = this.onSubmit.bind(this);
      this.boundOnButtonClick = this.onButtonClick.bind(this);
      this.boundOnInvalidFieldInput = this.onInvalidFieldInput.bind(this);
    }

    connectedCallback() {
      this.form = this.querySelector('form');
      if (!this.form) return;

      this.form.setAttribute('novalidate', '');
      this.fields = this.querySelectorAll('[data-validate]');
      this.submitButton = this.form.querySelector('[type="submit"]');

      this.form.addEventListener('submit', this.boundOnSubmit);
      if (this.submitButton) {
        this.submitButton.addEventListener('click', this.boundOnButtonClick);
      }
    }

    disconnectedCallback() {
      if (this.form) {
        this.form.removeEventListener('submit', this.boundOnSubmit);
      }
      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.boundOnButtonClick);
      }
    }

    onButtonClick(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.submitButton.disabled) return;

      const isValid = this.validateAll();

      if (!isValid) {
        this.disableSubmit();
        this.handleInvalidSubmit();
        return;
      }

      this.submitForm();
    }

    onSubmit(event) {
      event.preventDefault();
      event.stopPropagation();

      if (this.submitButton && this.submitButton.disabled) return;

      const isValid = this.validateAll();

      if (!isValid) {
        this.disableSubmit();
        this.handleInvalidSubmit();
        return;
      }

      this.submitForm();
    }

    handleInvalidSubmit() {
      const firstInvalid = this.querySelector('[data-validate][aria-invalid="true"]');
      if (firstInvalid) {
        firstInvalid.focus();
      }
    }

    disableSubmit() {
      if (this.submitButton) {
        this.submitButton.disabled = true;
        this.submitButton.setAttribute('aria-disabled', 'true');
      }

      this.invalidFields.clear();
      const invalidFieldElements = this.querySelectorAll('[data-validate][aria-invalid="true"]');
      invalidFieldElements.forEach((field) => {
        this.invalidFields.add(field);
        field.addEventListener('input', this.boundOnInvalidFieldInput);
      });
    }

    enableSubmit() {
      if (this.submitButton) {
        this.submitButton.disabled = false;
        this.submitButton.removeAttribute('aria-disabled');
      }
    }

    onInvalidFieldInput(event) {
      const field = event.target;
      field.removeEventListener('input', this.boundOnInvalidFieldInput);
      this.setFieldError(field, null);
      this.invalidFields.delete(field);

      if (this.invalidFields.size === 0) {
        this.enableSubmit();
      }
    }

    submitForm() {
      this.form.removeEventListener('submit', this.boundOnSubmit);
      if (this.submitButton) {
        this.submitButton.removeEventListener('click', this.boundOnButtonClick);
      }
      this.form.submit();
    }

    validateAll() {
      let isValid = true;

      this.fields.forEach((field) => {
        if (!this.validateField(field)) {
          isValid = false;
        }
      });

      return isValid;
    }

    validateField(field) {
      const rules = this.parseValidationRules(field);
      const value = field.value.trim();
      let errorMessage = null;

      if (rules.required && !value) {
        errorMessage = rules.requiredMessage || this.getDefaultMessage('required', field);
      }

      if (!errorMessage && value && rules.pattern) {
        const regex = new RegExp(rules.pattern);
        if (!regex.test(value)) {
          errorMessage = rules.patternMessage || this.getDefaultMessage('pattern', field);
        }
      }

      if (!errorMessage && value && rules.minlength) {
        if (value.length < parseInt(rules.minlength, 10)) {
          errorMessage = rules.minlengthMessage || this.getDefaultMessage('minlength', field, rules.minlength);
        }
      }

      if (!errorMessage && value && rules.maxlength) {
        if (value.length > parseInt(rules.maxlength, 10)) {
          errorMessage = rules.maxlengthMessage || this.getDefaultMessage('maxlength', field, rules.maxlength);
        }
      }

      if (!errorMessage && value && rules.type === 'email') {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) {
          errorMessage = rules.typeMessage || this.getDefaultMessage('email', field);
        }
      }

      if (!errorMessage && value && rules.type === 'tel') {
        const telRegex = /^[\d\s+\-()]{6,20}$/;
        if (!telRegex.test(value)) {
          errorMessage = rules.typeMessage || this.getDefaultMessage('tel', field);
        }
      }

      this.setFieldError(field, errorMessage);

      return !errorMessage;
    }

    parseValidationRules(field) {
      const rules = {};
      const validateAttr = field.dataset.validate;

      if (validateAttr) {
        const ruleList = validateAttr.split(' ');
        ruleList.forEach((rule) => {
          rules[rule] = true;
        });
      }

      if (field.dataset.validatePattern) {
        rules.pattern = field.dataset.validatePattern;
      }
      if (field.dataset.validateMinlength) {
        rules.minlength = field.dataset.validateMinlength;
      }
      if (field.dataset.validateMaxlength) {
        rules.maxlength = field.dataset.validateMaxlength;
      }
      if (field.dataset.validateType) {
        rules.type = field.dataset.validateType;
      }

      if (field.dataset.validateRequiredMessage) {
        rules.requiredMessage = field.dataset.validateRequiredMessage;
      }
      if (field.dataset.validatePatternMessage) {
        rules.patternMessage = field.dataset.validatePatternMessage;
      }
      if (field.dataset.validateTypeMessage) {
        rules.typeMessage = field.dataset.validateTypeMessage;
      }
      if (field.dataset.validateMinlengthMessage) {
        rules.minlengthMessage = field.dataset.validateMinlengthMessage;
      }
      if (field.dataset.validateMaxlengthMessage) {
        rules.maxlengthMessage = field.dataset.validateMaxlengthMessage;
      }

      return rules;
    }

    getDefaultMessage(type, field, param = null) {
      const validationStrings = window.validationStrings || {};
      const fieldName = this.getFieldLabel(field);

      const messages = {
        required: validationStrings.required?.replace('[field]', fieldName) || `${fieldName} is required`,
        pattern: validationStrings.pattern?.replace('[field]', fieldName) || `${fieldName} format is invalid`,
        email: validationStrings.email || `Please enter a valid email address`,
        tel: validationStrings.tel || `Please enter a valid phone number`,
        minlength:
          validationStrings.minlength?.replace('[field]', fieldName).replace('[count]', param) ||
          `${fieldName} must be at least ${param} characters`,
        maxlength:
          validationStrings.maxlength?.replace('[field]', fieldName).replace('[count]', param) ||
          `${fieldName} must be no more than ${param} characters`
      };

      return messages[type] || validationStrings.invalid || 'This field is invalid';
    }

    getFieldLabel(field) {
      const id = field.id;
      if (id) {
        const label = this.querySelector(`label[for="${id}"]`);
        if (label) {
          return label.textContent.replace('*', '').trim();
        }
      }

      return field.placeholder || field.name || 'This field';
    }

    setFieldError(field, errorMessage) {
      const fieldId = field.id;
      const errorContainerId = `${fieldId}-error`;
      let errorContainer = this.querySelector(`#${errorContainerId}`);

      if (errorMessage) {
        field.setAttribute('aria-invalid', 'true');
        field.setAttribute('aria-describedby', errorContainerId);
        field.classList.add('field__input--error');

        if (errorContainer) {
          errorContainer.innerHTML = `
            <a href="#${fieldId}" class="link form-status form-status--error">
              ${errorMessage}
            </a>
          `;
          errorContainer.style.display = '';
          errorContainer.removeAttribute('hidden');
        }
      } else {
        field.removeAttribute('aria-invalid');
        field.removeAttribute('aria-describedby');
        field.classList.remove('field__input--error');

        if (errorContainer) {
          errorContainer.innerHTML = '';
          errorContainer.style.display = 'none';
          errorContainer.setAttribute('hidden', '');
        }
      }
    }
  }

  customElements.define('form-validator', FormValidator);
}
